import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { waLink } from "@/lib/contact";
import { MessageCircle, Truck, ShieldCheck, Ruler } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, tr, L, priceText, catLabel } = useLang();
  const [data, setData] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => {
      setData(r.data);
      setSize(r.data.product.sizes?.[0] || "");
      setColor(r.data.product.colors?.[0]?.name || "");
      setActiveImg(0);
    }).catch(() => navigate("/catalogo"));
  }, [slug, navigate]);

  if (!data) return <div className="pt-32 pb-40 text-center text-white/40 font-display uppercase tracking-widest">…</div>;
  const { product: p, worn_by, related } = data;
  const pname = L(p, "name");
  const waMsg = `${t("whatsapp_product")} ${pname} (${color}${size ? " / " + size : ""})`;

  return (
    <div data-testid="product-detail-page" className="pt-16 lg:pt-20">
      <Seo
        title={`${pname} | VORNA`}
        description={(L(p, "description") || "").slice(0, 160)}
        path={`/producto/${p.slug}`}
        image={p.images?.[0]}
      />
      <div className="mx-auto max-w-[1600px] px-5 lg:px-10 py-8 lg:py-14">
        <div className="text-xs text-white/40 tracking-widest mb-8">
          <Link to="/catalogo" className="hover:text-white">{t("nav_catalog").toUpperCase()}</Link> / <Link to={`/catalogo?category=${p.category}`} className="hover:text-white uppercase">{catLabel(p.category)}</Link> / <span className="text-white">{p.name}</span>
        </div>

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-16">
          <div className="grid grid-cols-1 gap-3">
            <Dialog>
              <DialogTrigger asChild>
                <button className="relative aspect-[4/5] overflow-hidden bg-vorna-elevated cursor-zoom-in">
                  <img data-testid="product-main-image" src={p.images[activeImg]} alt={p.name} className="h-full w-full object-cover" />
                </button>
              </DialogTrigger>
              <DialogContent className="rounded-none bg-black border-white/15 max-w-3xl p-2">
                <img src={p.images[activeImg]} alt={p.name} className="w-full h-auto object-contain" />
              </DialogContent>
            </Dialog>
            {p.images.length > 1 && (
              <div className="grid grid-cols-5 gap-3">
                {p.images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square overflow-hidden bg-vorna-elevated border ${activeImg === i ? "border-vorna-accent" : "border-transparent"}`}>
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:sticky lg:top-28 lg:self-start">
            <p className="text-[11px] tracking-widest text-vorna-accent uppercase mb-2">{catLabel(p.category)}</p>
            <h1 className="font-display uppercase tracking-tighter text-4xl lg:text-6xl leading-[0.85]">{pname}</h1>
            <div className="mt-4 flex items-center gap-3">
              <p data-testid="product-price" className="font-display text-xl text-white/80">{priceText(p.price)}</p>
              {p.colors?.length > 0 && (
                <div className="flex items-center gap-1.5">
                  {p.colors.map((c) => (
                    <span key={c.name} data-testid={`color-dot-${c.name}`} className="h-4 w-4 rounded-full border border-white/25" style={{ backgroundColor: c.hex }} title={c.name} />
                  ))}
                </div>
              )}
            </div>
            {L(p, "price_note") && <p data-testid="product-price-note" className="mt-2 text-sm text-white/50">{L(p, "price_note")}</p>}
            {L(p, "description") && <p className="mt-5 text-white/60 leading-relaxed">{L(p, "description")}</p>}

            {p.colors?.length > 0 && (
            <div className="mt-8">
              <p className="text-[11px] tracking-widest text-white/40 uppercase mb-3">{t("colors_available")}: <span className="text-white">{color}</span></p>
              <div className="flex gap-3">
                {p.colors.map((c) => (
                  <button key={c.name} data-testid={`color-${c.name}`} onClick={() => setColor(c.name)} title={c.name}
                    className={`h-9 w-9 rounded-full border-2 transition-transform hover:scale-105 ${color === c.name ? "border-vorna-accent scale-110" : "border-white/25"}`} style={{ backgroundColor: c.hex }} />
                ))}
              </div>
            </div>
            )}

            {p.sizes?.length > 0 && (
            <div className="mt-7">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] tracking-widest text-white/40 uppercase">{t("sizes_available")}: <span className="text-white">{size}</span></p>
                <Dialog>
                  <DialogTrigger asChild>
                    <button data-testid="size-guide-trigger" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-vorna-accent"><Ruler className="h-3.5 w-3.5" /> {t("size_guide")}</button>
                  </DialogTrigger>
                  <DialogContent className="rounded-none bg-vorna-surface border-white/15 text-white max-w-lg">
                    <DialogHeader><DialogTitle className="font-display uppercase tracking-tight text-2xl">{t("size_guide")}</DialogTitle></DialogHeader>
                    <table className="w-full text-sm mt-2">
                      <thead><tr className="border-b border-white/15 text-white/50 text-xs tracking-widest"><th className="py-2 text-left">{tr("TALLA","SIZE")}</th><th className="py-2">{tr("PECHO (cm)","CHEST (cm)")}</th><th className="py-2">{tr("CINTURA (cm)","WAIST (cm)")}</th></tr></thead>
                      <tbody>
                        {[["XS","81-86","66-71"],["S","86-94","71-79"],["M","97-104","81-86"],["L","107-114","89-97"],["XL","117-124","99-107"],["XXL","127-135","109-117"]].map((r) => (
                          <tr key={r[0]} className="border-b border-white/5"><td className="py-2 font-semibold">{r[0]}</td><td className="py-2 text-center text-white/60">{r[1]}</td><td className="py-2 text-center text-white/60">{r[2]}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map((s) => (
                  <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)}
                    className={`h-11 min-w-11 px-3 font-semibold text-sm border transition-colors ${size === s ? "border-vorna-accent bg-vorna-accent text-white" : "border-white/20 hover:border-white/50"}`}>{s}</button>
                ))}
              </div>
            </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a data-testid="whatsapp-inquiry-button" href={waLink(waMsg)} target="_blank" rel="noreferrer"
                className="flex-1 bg-vorna-accent py-4 font-display uppercase tracking-widest text-sm hover:bg-vorna-accentHover active:scale-95 transition-all flex items-center justify-center gap-2">
                <MessageCircle className="h-4 w-4" /> {t("check_availability")}
              </a>
              <Link data-testid="contact-button" to="/contacto"
                className="flex-1 border border-white/25 py-4 font-display uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors flex items-center justify-center">
                {t("contact_vorna")}
              </Link>
            </div>
            <p className="mt-3 text-xs text-white/40">{tr("Consulta disponibilidad, precio y envío directamente con VORNA.", "Ask VORNA directly about availability, pricing and delivery.")}</p>

            <div className="mt-6 grid grid-cols-2 gap-3 text-xs text-white/50">
              <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-vorna-accent" /> {tr("Desde Quito, Ecuador", "From Quito, Ecuador")}</div>
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-vorna-accent" /> {tr("Producto oficial VORNA", "Official VORNA product")}</div>
            </div>

            <Accordion type="single" collapsible className="mt-8 border-t border-white/10">
              {L(p, "material") && <Item value="material" title={t("materials")}>{L(p, "material")}</Item>}
              {(p.features?.length > 0) && (
                <AccordionItem value="features" className="border-white/10">
                  <AccordionTrigger className="font-display uppercase tracking-widest text-sm hover:no-underline">{t("features")}</AccordionTrigger>
                  <AccordionContent><ul className="space-y-2 text-white/60 text-sm">{L(p, "features").map((f) => <li key={f} className="flex gap-2"><span className="text-vorna-accent">—</span>{f}</li>)}</ul></AccordionContent>
                </AccordionItem>
              )}
              {L(p, "intended_use") && <Item value="use" title={t("intended_use")}>{L(p, "intended_use")}</Item>}
              {L(p, "care") && <Item value="care" title={t("care")}>{L(p, "care")}</Item>}
            </Accordion>
          </div>
        </div>

        {worn_by?.length > 0 && (
          <section className="mt-24 lg:mt-32">
            <Reveal><p className="text-xs tracking-[0.35em] text-vorna-accent uppercase mb-3">{t("worn_by")}</p><h2 className="font-display uppercase tracking-tighter text-3xl lg:text-5xl leading-[0.85] mb-10">{tr("En la jaula", "In the cage")}</h2></Reveal>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {worn_by.map((f) => (
                <Link key={f.id} to={`/atletas/${f.id}`} className="group relative block aspect-[3/4] overflow-hidden bg-vorna-elevated">
                  <img src={f.image} alt={f.name} className="h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-4"><p className="font-display uppercase text-sm">{f.name}</p><p className="text-[11px] text-vorna-accent">"{f.nickname}"</p></div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {related?.length > 0 && (
          <section className="mt-24 lg:mt-32">
            <Reveal><h2 className="font-display uppercase tracking-tighter text-3xl lg:text-5xl leading-[0.85] mb-10">{t("you_may_like")}</h2></Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">{related.map((r) => <ProductCard key={r.id} product={r} />)}</div>
          </section>
        )}
      </div>

      {/* sticky mobile inquiry */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-black/90 backdrop-blur-md border-t border-white/10 p-3 flex items-center gap-3">
        <div className="flex flex-col"><span className="text-[10px] text-white/40 tracking-widest">{size} / {color}</span><span className="font-display text-sm">{priceText(p.price)}</span></div>
        <a data-testid="sticky-whatsapp" href={waLink(waMsg)} target="_blank" rel="noreferrer" className="flex-1 bg-vorna-accent py-3.5 font-display uppercase tracking-widest text-sm active:scale-95 transition-transform flex items-center justify-center gap-2"><MessageCircle className="h-4 w-4" /> {t("check_availability")}</a>
      </div>
    </div>
  );
}

function Item({ value, title, children }) {
  return (
    <AccordionItem value={value} className="border-white/10">
      <AccordionTrigger className="font-display uppercase tracking-widest text-sm hover:no-underline">{title}</AccordionTrigger>
      <AccordionContent className="text-white/60 text-sm leading-relaxed">{children}</AccordionContent>
    </AccordionItem>
  );
}
